/**
 * Budget Service
 * Handles budget creation, variance analysis, and budget vs actual reporting
 */

import { SupabaseClient } from '@supabase/supabase-js';
import {
  Budget,
  BudgetLineItem,
  BudgetMonthlyBreakdown,
  CreateBudgetInput,
  UpdateBudgetInput,
  BudgetVsActual,
  BudgetSummary,
  ServiceResponse,
  BudgetStatus,
  AccountType,
} from '../../types/financial';

export class BudgetService {
  constructor(private supabase: SupabaseClient) {}

  /**
   * Create a new budget
   */
  async createBudget(input: CreateBudgetInput): Promise<ServiceResponse<Budget>> {
    try {
      // 1. Validate input
      if (input.line_items.length === 0) {
        return {
          success: false,
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Budget must have at least one line item',
          },
        };
      }

      // 2. Get current user
      const {
        data: { user },
      } = await this.supabase.auth.getUser();

      // 3. Calculate total budgeted amount
      const totalBudgeted = input.line_items.reduce(
        (sum, item) => sum + item.budgeted_amount,
        0
      );

      // 4. Create budget
      const { data: budget, error: budgetError } = await this.supabase
        .from('budgets')
        .insert({
          church_id: input.church_id,
          budget_name: input.budget_name,
          budget_year: input.budget_year,
          start_date: input.start_date.toISOString().split('T')[0],
          end_date: input.end_date.toISOString().split('T')[0],
          status: BudgetStatus.DRAFT,
          total_budgeted_amount: totalBudgeted,
          total_actual_amount: 0,
          notes: input.notes,
          created_by: user?.id,
        })
        .select()
        .single();

      if (budgetError) {
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to create budget',
            details: budgetError,
          },
        };
      }

      // 5. Create budget line items
      const lineItemsData = input.line_items.map((item) => ({
        budget_id: budget.id,
        account_id: item.account_id,
        budgeted_amount: item.budgeted_amount,
        actual_amount: 0,
        variance: 0,
        variance_percent: 0,
        notes: item.notes,
      }));

      const { data: lineItems, error: lineItemsError } = await this.supabase
        .from('budget_line_items')
        .insert(lineItemsData)
        .select();

      if (lineItemsError) {
        // Rollback budget creation
        await this.supabase.from('budgets').delete().eq('id', budget.id);
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to create budget line items',
            details: lineItemsError,
          },
        };
      }

      // 6. Create monthly breakdowns if provided
      for (let i = 0; i < input.line_items.length; i++) {
        const lineItem = input.line_items[i];
        if (lineItem.monthly_breakdown && lineItem.monthly_breakdown.length > 0) {
          const monthlyData = lineItem.monthly_breakdown.map((month) => ({
            budget_line_item_id: lineItems[i].id,
            month_number: month.month_number,
            budgeted_amount: month.budgeted_amount,
            actual_amount: 0,
          }));

          await this.supabase.from('budget_monthly_breakdown').insert(monthlyData);
        }
      }

      return {
        success: true,
        data: { ...budget, line_items: lineItems } as Budget,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNEXPECTED_ERROR',
          message: 'An unexpected error occurred',
          details: error,
        },
      };
    }
  }

  /**
   * Get budget by ID with line items
   */
  async getBudget(budgetId: string): Promise<ServiceResponse<Budget>> {
    try {
      const { data: budget, error } = await this.supabase
        .from('budgets')
        .select(
          `
          *,
          budget_line_items (
            *,
            budget_monthly_breakdown (*)
          )
        `
        )
        .eq('id', budgetId)
        .single();

      if (error || !budget) {
        return {
          success: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Budget not found',
          },
        };
      }

      return {
        success: true,
        data: budget as Budget,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNEXPECTED_ERROR',
          message: 'An unexpected error occurred',
          details: error,
        },
      };
    }
  }

  /**
   * Update budget actuals from general ledger
   * This refreshes the actual amounts for all line items
   */
  async updateBudgetActuals(budgetId: string): Promise<ServiceResponse<Budget>> {
    try {
      // Call the database function to update actuals
      const { error } = await this.supabase.rpc('update_budget_actuals', {
        p_budget_id: budgetId,
      });

      if (error) {
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to update budget actuals',
            details: error,
          },
        };
      }

      // Fetch and return updated budget
      return this.getBudget(budgetId);
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNEXPECTED_ERROR',
          message: 'An unexpected error occurred',
          details: error,
        },
      };
    }
  }

  /**
   * Activate a budget (make it the active budget for the year)
   */
  async activateBudget(budgetId: string, approverId: string): Promise<ServiceResponse<Budget>> {
    try {
      // 1. Get budget to check church and year
      const { data: budget } = await this.supabase
        .from('budgets')
        .select('church_id, budget_year')
        .eq('id', budgetId)
        .single();

      if (!budget) {
        return {
          success: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Budget not found',
          },
        };
      }

      // 2. Deactivate any other active budgets for this year
      await this.supabase
        .from('budgets')
        .update({ status: BudgetStatus.CLOSED })
        .eq('church_id', budget.church_id)
        .eq('budget_year', budget.budget_year)
        .eq('status', BudgetStatus.ACTIVE);

      // 3. Activate this budget
      const { data: updated, error } = await this.supabase
        .from('budgets')
        .update({
          status: BudgetStatus.ACTIVE,
          approved_by: approverId,
          approved_at: new Date().toISOString(),
        })
        .eq('id', budgetId)
        .select()
        .single();

      if (error) {
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to activate budget',
            details: error,
          },
        };
      }

      return {
        success: true,
        data: updated as Budget,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNEXPECTED_ERROR',
          message: 'An unexpected error occurred',
          details: error,
        },
      };
    }
  }

  /**
   * Get budget vs actual report
   */
  async getBudgetVsActual(budgetId: string): Promise<ServiceResponse<BudgetVsActual[]>> {
    try {
      const { data, error } = await this.supabase
        .from('budget_vs_actual')
        .select('*')
        .eq('budget_id', budgetId)
        .order('account_code');

      if (error) {
        return {
          success: false,
          error: {
            code: 'FETCH_ERROR',
            message: 'Failed to fetch budget vs actual',
            details: error,
          },
        };
      }

      return {
        success: true,
        data: data as BudgetVsActual[],
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNEXPECTED_ERROR',
          message: 'An unexpected error occurred',
          details: error,
        },
      };
    }
  }

  /**
   * Get budget summary with variance analysis
   */
  async getBudgetSummary(budgetId: string): Promise<ServiceResponse<BudgetSummary>> {
    try {
      // Get budget with line items
      const budgetResult = await this.getBudget(budgetId);
      if (!budgetResult.success || !budgetResult.data) {
        return {
          success: false,
          error: budgetResult.error,
        };
      }

      const budget = budgetResult.data;
      const lineItems = budget.line_items || [];

      // Get account types for each line item
      const accountIds = lineItems.map((item) => item.account_id);
      const { data: accounts } = await this.supabase
        .from('chart_of_accounts')
        .select('id, account_type')
        .in('id', accountIds);

      const accountTypeMap = new Map(
        accounts?.map((acc) => [acc.id, acc.account_type]) || []
      );

      // Calculate summary
      let revenueBudgeted = 0;
      let revenueActual = 0;
      let expenseBudgeted = 0;
      let expenseActual = 0;
      let overBudgetCount = 0;
      let underBudgetCount = 0;
      let onTrackCount = 0;

      for (const item of lineItems) {
        const accountType = accountTypeMap.get(item.account_id);

        if (accountType === AccountType.REVENUE) {
          revenueBudgeted += item.budgeted_amount;
          revenueActual += item.actual_amount;
        } else if (accountType === AccountType.EXPENSE) {
          expenseBudgeted += item.budgeted_amount;
          expenseActual += item.actual_amount;
        }

        // Count variance status
        if (item.variance_percent > 10) {
          overBudgetCount++;
        } else if (item.variance_percent < -10) {
          underBudgetCount++;
        } else {
          onTrackCount++;
        }
      }

      const totalVariance = budget.total_actual_amount - budget.total_budgeted_amount;
      const variancePercent =
        budget.total_budgeted_amount !== 0
          ? (totalVariance / budget.total_budgeted_amount) * 100
          : 0;

      const summary: BudgetSummary = {
        budget_id: budget.id,
        budget_name: budget.budget_name,
        budget_year: budget.budget_year,
        total_budgeted: budget.total_budgeted_amount,
        total_actual: budget.total_actual_amount,
        total_variance: totalVariance,
        variance_percent: variancePercent,
        revenue_budgeted: revenueBudgeted,
        revenue_actual: revenueActual,
        expense_budgeted: expenseBudgeted,
        expense_actual: expenseActual,
        accounts_over_budget: overBudgetCount,
        accounts_under_budget: underBudgetCount,
        accounts_on_track: onTrackCount,
      };

      return {
        success: true,
        data: summary,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNEXPECTED_ERROR',
          message: 'An unexpected error occurred',
          details: error,
        },
      };
    }
  }

  /**
   * Get all budgets for a church
   */
  async getChurchBudgets(
    churchId: string,
    year?: number
  ): Promise<ServiceResponse<Budget[]>> {
    try {
      let query = this.supabase
        .from('budgets')
        .select('*')
        .eq('church_id', churchId)
        .order('budget_year', { ascending: false });

      if (year) {
        query = query.eq('budget_year', year);
      }

      const { data, error } = await query;

      if (error) {
        return {
          success: false,
          error: {
            code: 'FETCH_ERROR',
            message: 'Failed to fetch budgets',
            details: error,
          },
        };
      }

      return {
        success: true,
        data: (data || []) as Budget[],
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNEXPECTED_ERROR',
          message: 'An unexpected error occurred',
          details: error,
        },
      };
    }
  }

  /**
   * Check budget availability before approving expense
   * Returns true if within budget, false if over budget
   */
  async checkBudgetAvailability(
    accountId: string,
    amount: number,
    churchId: string
  ): Promise<ServiceResponse<{ available: boolean; remaining: number }>> {
    try {
      // Get active budget for current year
      const currentYear = new Date().getFullYear();
      const { data: budgets } = await this.supabase
        .from('budgets')
        .select('id')
        .eq('church_id', churchId)
        .eq('budget_year', currentYear)
        .eq('status', BudgetStatus.ACTIVE)
        .limit(1);

      if (!budgets || budgets.length === 0) {
        // No active budget, allow transaction
        return {
          success: true,
          data: { available: true, remaining: 0 },
        };
      }

      // Get budget line item for this account
      const { data: lineItem } = await this.supabase
        .from('budget_line_items')
        .select('budgeted_amount, actual_amount')
        .eq('budget_id', budgets[0].id)
        .eq('account_id', accountId)
        .single();

      if (!lineItem) {
        // No budget for this account, allow transaction
        return {
          success: true,
          data: { available: true, remaining: 0 },
        };
      }

      const remaining = lineItem.budgeted_amount - lineItem.actual_amount;
      const available = remaining >= amount;

      return {
        success: true,
        data: { available, remaining },
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNEXPECTED_ERROR',
          message: 'An unexpected error occurred',
          details: error,
        },
      };
    }
  }
}
